import {
   useGetActiveLeadSource,
   useGetActiveLeadSourceDetails,
} from '../../hooks'
import { useGetFubPonds } from '@forwardflow/hooks/customHooks/stageMapping/fubPonds'
import { useAdvanceTargetingCampaignModalStore } from '@forwardflow/store/modal/AdvanceTargetingCampaignModal'
import Icon from '@forwardflow/ui/Icons'
import { getFilterIcon } from '@forwardflow/utils/filterIcons'
import { Skeleton } from '@forwardflow/ui-v2'
import { useParams, useNavigate, useLocation } from 'react-router-dom'

const FilterRow = ({
   icon,
   label,
   values,
}: {
   icon: React.ReactNode
   label: string
   values: string[]
}) => (
   <div className="space-y-2">
      <div className="flex items-center gap-x-2">
         <span className="shrink-0 opacity-60">{icon}</span>
         <span className="text-white/50 text-xs">{label}</span>
      </div>
      <div className="flex flex-wrap gap-1.5 pl-[26px]">
         {values.map((value, i) => (
            <span
               key={i}
               className="inline-flex items-center px-2.5 py-1 rounded-md bg-[#9061DA]/15 text-white text-xs"
            >
               {value}
            </span>
         ))}
      </div>
   </div>
)

export const CampaignFilterDetails = () => {
   const { title: leadSource } = useParams()
   const navigate = useNavigate()
   const location = useLocation()
   const { data: activeLeadSourceData, isLoading } = useGetActiveLeadSource(
      leadSource as string
   )

   const hasCampaignFiltersChanges =
      activeLeadSourceData?.draft?.changedSections?.includes('campaign_filters')

   const { data: campaignDetails } = useGetActiveLeadSourceDetails(
      leadSource as string
   )

   const { data: pondList } = useGetFubPonds({
      page: 1,
      limit: 10000,
      search: '',
   })

   const dataSource = hasCampaignFiltersChanges
      ? campaignDetails
      : activeLeadSourceData

   const eligibleCriteria = dataSource?.data?.campaignFilters?.eligibleCriteria
   const sourceCriteria = Array.isArray(eligibleCriteria)
      ? eligibleCriteria.find((criteria: any) => criteria.field === 'source')
      : null
   const stageCriteria = Array.isArray(eligibleCriteria)
      ? eligibleCriteria.find((criteria: any) => criteria.field === 'stage')
      : null
   const tagsCriteria = Array.isArray(eligibleCriteria)
      ? eligibleCriteria.find((criteria: any) => criteria.field === 'tags')
      : null
   const createdCriteria = Array.isArray(eligibleCriteria)
      ? eligibleCriteria.find(
           (criteria: any) => criteria.field === 'created_at'
        )
      : null
   const ageCriteria = Array.isArray(eligibleCriteria)
      ? eligibleCriteria.find((criteria: any) => criteria.field === 'age')
      : null
   const pondsCriteria = Array.isArray(eligibleCriteria)
      ? eligibleCriteria.find(
           (criteria: any) => criteria.field === 'assigned_pond_id'
        )
      : null
   const inquiredPropertyCriteria = Array.isArray(eligibleCriteria)
      ? eligibleCriteria.find(
           (criteria: any) => criteria.field === 'inquired_property'
        )
      : null
   const sources = Array.isArray(sourceCriteria?.value)
      ? sourceCriteria.value
      : sourceCriteria?.value
        ? [sourceCriteria.value]
        : []
   const stages = Array.isArray(stageCriteria?.value)
      ? stageCriteria.value
      : stageCriteria?.value
        ? [stageCriteria.value]
        : []
   const tags = Array.isArray(tagsCriteria?.value)
      ? tagsCriteria.value
      : tagsCriteria?.value
        ? [tagsCriteria.value]
        : []
   const created = createdCriteria ? createdCriteria.value : null
   const createdOperator = createdCriteria?.operator || ''
   const age = ageCriteria ? ageCriteria.value : null
   const ageOperator = ageCriteria?.operator || ''
   const ponds = pondsCriteria?.value || []
   const inquiredProperties = Array.isArray(inquiredPropertyCriteria?.value)
      ? inquiredPropertyCriteria.value
      : inquiredPropertyCriteria?.value
        ? [inquiredPropertyCriteria.value]
        : []
   const { openAdvanceTargetingCampaignModal } =
      useAdvanceTargetingCampaignModalStore()

   const handleEditClick = () => {
      const searchParams = new URLSearchParams(location.search)
      searchParams.set('edit', 'true')
      navigate(`${location.pathname}?${searchParams.toString()}`, {
         replace: true,
      })

      openAdvanceTargetingCampaignModal()
   }

   if (isLoading && sources.length === 0 && eligibleCriteria?.length === 0) {
      return (
         <div className="rounded-lg bg-white/[0.05] p-5 space-y-4">
            <div className="flex items-center justify-between">
               <Skeleton height={12} width={100} radius="sm" />
               <Skeleton height={20} width={20} radius="sm" />
            </div>
            {[...Array(3)].map((_, i) => (
               <div key={i} className="space-y-2">
                  <Skeleton height={10} width={60} radius="sm" />
                  <div className="flex gap-1.5 pl-[26px]">
                     <Skeleton height={24} width={70} radius="md" />
                     <Skeleton height={24} width={50} radius="md" />
                  </div>
               </div>
            ))}
         </div>
      )
   }

   if (eligibleCriteria?.length === 0) {
      return null
   }

   const createdValue =
      createdOperator === 'BETWEEN' &&
      Array.isArray(created) &&
      created.length === 2
         ? `Between ${created[0]} and ${created[1]}`
         : `${
              createdOperator === '<='
                 ? 'Before'
                 : createdOperator === '>='
                   ? 'After'
                   : createdOperator
           } ${created}`

   const ageValue = `${
      ageOperator === '<=' || ageOperator === '<'
         ? 'Less than'
         : ageOperator === '>=' || ageOperator === '>'
           ? 'Greater than'
           : ageOperator
   } ${age}`

   const pondValues = ponds.map((pondId: any) => {
      const pond = pondList?.find((p: any) => p.pondId === Number(pondId))
      return pond?.pondName || pondId
   })

   return (
      <div className="rounded-lg bg-white/[0.05] p-5">
         {/* Header */}
         <div className="flex items-center justify-between mb-4">
            <p className="text-white/50 text-xs">Campaign Filters</p>
            <button
               onClick={handleEditClick}
               className="p-1.5 rounded-md transition-all text-white/40 hover:text-white hover:bg-white/10"
            >
               <Icon name="Edit" className="w-4 h-4" />
            </button>
         </div>

         <div className="space-y-4">
            {sources.length > 0 && (
               <FilterRow
                  icon={getFilterIcon('source')}
                  label="Sources"
                  values={sources}
               />
            )}

            {stages.length > 0 && (
               <FilterRow
                  icon={getFilterIcon('stages')}
                  label="Stage"
                  values={stages}
               />
            )}

            {tags.length > 0 && (
               <FilterRow
                  icon={getFilterIcon('tags')}
                  label="Tags"
                  values={tags}
               />
            )}

            {created && (
               <FilterRow
                  icon={getFilterIcon('created')}
                  label="Created"
                  values={[createdValue]}
               />
            )}

            {age && (
               <FilterRow
                  icon={getFilterIcon('age')}
                  label="Age"
                  values={[ageValue]}
               />
            )}

            {ponds.length > 0 && (
               <FilterRow
                  icon={getFilterIcon('Ponds')}
                  label="Ponds"
                  values={pondValues}
               />
            )}

            {inquiredProperties.length > 0 && (
               <FilterRow
                  icon={getFilterIcon('inquired_property')}
                  label="Inquired Properties"
                  values={inquiredProperties}
               />
            )}
         </div>
      </div>
   )
}
